import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { StateBadge } from '../components/StateBadge';
import { GlassCard } from '../components/GlassCard';
import { TaskCardSkeleton } from '../components/Skeleton';
import { T } from '../lib/design-tokens';
import { TaskState } from '../types';
import type { QueryLogRecord, ClassificationResult } from '../types';
import { api } from '../lib/api';

const TERMINAL_STATES = new Set([
  TaskState.COMPLETED,
  TaskState.FAILED,
  TaskState.CANCELLED,
  TaskState.NO_RESPONSE,
]);

const TASK_MESSAGE_TYPES = new Set(['classification', 'approval', 'execution', 'result']);

type Filter = 'all' | 'active' | 'completed';

interface TaskSummary {
  taskId: string;
  goal: string;
  company: string | null;
  state: string;
  createdAt: string;
  channel: string | null;
}

function timeAgo(ts: string) {
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return days === 1 ? 'Yesterday' : `${days}d ago`;
}

function deriveState(logs: QueryLogRecord[]): string {
  const latest = logs[logs.length - 1];
  if (!latest) return 'CLASSIFIED';
  const action = latest.action?.toLowerCase() ?? '';
  if (action.includes('completed') || action.includes('complete')) return 'COMPLETED';
  if (action.includes('failed') || action.includes('fail')) return 'FAILED';
  if (action.includes('cancelled') || action.includes('cancel')) return 'CANCELLED';
  if (action.includes('no_response')) return 'NO_RESPONSE';
  if (action.includes('waiting')) return 'WAITING_RESPONSE';
  if (action.includes('executing') || action.includes('execution') || latest.messageType === 'execution') return 'EXECUTING';
  if (action.includes('approved') || action.includes('approval') || latest.messageType === 'approval') return 'APPROVED';
  if (latest.messageType === 'result') return 'COMPLETED';
  return 'AWAITING_APPROVAL';
}

export function OperationsPage() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState<QueryLogRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>('all');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const LIMIT = 50;

  const fetchLogs = useCallback(async (loadOffset: number, append: boolean) => {
    try {
      const data = await api<{ logs: QueryLogRecord[]; limit: number; offset: number }>(
        `/history?limit=${LIMIT}&offset=${loadOffset}`,
      );
      const newLogs = data.logs;
      setLogs((prev) => (append ? [...prev, ...newLogs] : newLogs));
      setHasMore(newLogs.length >= LIMIT);
      setOffset(loadOffset + newLogs.length);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs(0, false);
  }, [fetchLogs]);

  const handleLoadMore = () => {
    setLoadingMore(true);
    fetchLogs(offset, true);
  };

  const taskMap = new Map<string, QueryLogRecord[]>();
  for (const log of logs) {
    if (!log.taskId || !TASK_MESSAGE_TYPES.has(log.messageType)) continue;
    const existing = taskMap.get(log.taskId);
    if (existing) {
      existing.push(log);
    } else {
      taskMap.set(log.taskId, [log]);
    }
  }

  const tasks: TaskSummary[] = [];
  for (const [taskId, taskLogs] of taskMap) {
    taskLogs.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    const first = taskLogs[0]!;
    const classification = first.classification as ClassificationResult | null;
    tasks.push({
      taskId,
      goal: classification?.goal ?? first.inputText ?? 'Task',
      company: classification?.company ?? null,
      state: deriveState(taskLogs),
      createdAt: first.timestamp,
      channel: classification?.channel ?? null,
    });
  }

  tasks.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const filtered = tasks.filter((t) => {
    if (filter === 'active') return !TERMINAL_STATES.has(t.state as TaskState);
    if (filter === 'completed') return TERMINAL_STATES.has(t.state as TaskState);
    return true;
  });

  const filters: { key: Filter; label: string }[] = [
    { key: 'all', label: 'all' },
    { key: 'active', label: 'active' },
    { key: 'completed', label: 'completed' },
  ];

  return (
    <div className="max-w-[760px] mx-auto" style={{ padding: '40px 40px' }}>
      <h1
        style={{
          fontFamily: T.serif,
          fontSize: 30,
          fontWeight: 400,
          color: T.cream,
          marginBottom: 24,
          animation: 'fadeUp 0.5s ease both',
        }}
      >
        Operations
      </h1>

      <div
        className="inline-flex gap-0.5 mb-6"
        style={{
          padding: 3,
          borderRadius: 10,
          background: T.cream04,
          width: 'fit-content',
        }}
      >
        {filters.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            style={{
              padding: '7px 16px',
              borderRadius: 8,
              border: 'none',
              background: filter === f.key ? T.cream08 : 'transparent',
              color: filter === f.key ? T.cream : T.cream35,
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: T.mono,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              transition: 'all 0.2s',
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="flex flex-col gap-2">
          {[0, 1, 2, 3].map((i) => (
            <TaskCardSkeleton key={i} />
          ))}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <GlassCard style={{ padding: 64, textAlign: 'center' }}>
          <div style={{ fontFamily: T.sans, fontSize: 14, fontWeight: 500, color: T.cream35, marginBottom: 8 }}>
            {filter === 'all'
              ? 'No operations yet'
              : filter === 'active'
                ? 'No active operations'
                : 'No completed operations'}
          </div>
          <div style={{ fontFamily: T.sans, fontSize: 12, color: T.cream20, marginBottom: 16 }}>
            Describe a task on the home page to get started.
          </div>
          <button
            type="button"
            onClick={() => navigate('/')}
            style={{
              padding: '9px 20px',
              borderRadius: 10,
              border: 'none',
              background: T.gold,
              color: T.bg,
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: T.mono,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.boxShadow = `0 0 20px ${T.goldGlow}`)}
            onMouseLeave={(e) => (e.currentTarget.style.boxShadow = 'none')}
          >
            Go to Home
          </button>
        </GlassCard>
      )}

      {!loading && filtered.length > 0 && (
        <div className="flex flex-col gap-2">
          {filtered.map((task, i) => {
            const stateEnum = task.state as TaskState;
            const isCompleted = stateEnum === TaskState.COMPLETED;
            const isWaiting = stateEnum === TaskState.WAITING_RESPONSE || stateEnum === TaskState.AWAITING_APPROVAL;
            const progressBg = isCompleted
              ? T.green
              : isWaiting
                ? T.gold
                : `linear-gradient(90deg, ${T.gold}, #e8c87c)`;
            const progressPct = isCompleted ? 100 : 50;

            return (
              <GlassCard
                key={task.taskId}
                hover
                onClick={() => navigate(`/operations/${task.taskId}`)}
                style={{
                  padding: '18px 20px',
                  animation: `fadeUp 0.4s ease ${i * 0.06}s both`,
                }}
              >
                <div className="flex justify-between items-start mb-2.5">
                  <div className="flex-1 min-w-0">
                    <div
                      style={{
                        fontFamily: T.sans,
                        fontSize: 14,
                        fontWeight: 500,
                        color: T.cream,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {task.goal}
                    </div>
                    <div style={{ fontFamily: T.mono, fontSize: 11, color: T.cream20, marginTop: 4 }}>
                      {[task.company, timeAgo(task.createdAt)].filter(Boolean).join(' \u00B7 ')}
                      {task.channel && ` \u00B7 ${task.channel}`}
                    </div>
                  </div>
                  <StateBadge state={task.state} />
                </div>
                <div
                  style={{
                    height: 2,
                    borderRadius: 1,
                    background: T.cream04,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      borderRadius: 1,
                      background: progressBg,
                      width: `${progressPct}%`,
                      transition: 'width 1s ease',
                    }}
                  />
                </div>
              </GlassCard>
            );
          })}
        </div>
      )}

      {!loading && hasMore && (
        <div className="text-center mt-6">
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={loadingMore}
            style={{
              padding: '9px 24px',
              borderRadius: 10,
              border: `1px solid ${T.cream08}`,
              background: 'transparent',
              color: T.cream60,
              fontSize: 13,
              fontWeight: 500,
              cursor: loadingMore ? 'default' : 'pointer',
              fontFamily: T.sans,
            }}
            onMouseEnter={(e) => {
              if (!loadingMore) {
                e.currentTarget.style.borderColor = T.cream12;
                e.currentTarget.style.color = T.cream;
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = T.cream08;
              e.currentTarget.style.color = T.cream60;
            }}
          >
            {loadingMore ? 'Loading...' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}
